// What the attacker's sign-in attempts actually achieved, and what the account's state was
// at the end of the case. Pure and dependency-light: calculateAttackerWindow computes these
// once, and the case screen, the Client/Technical reports and the Executive Brief all read
// the result, so they cannot word the same case differently.
//
// Origin: the pipeline team's accuracy review of the IR-20261013-levig-001 Brief
// (M365Signal-Databricks-Pipelines/handoff/2026-10-15_Portal-Exec-Brief-…-Accuracy-Review.md).
// The Brief said the attacker "briefly accessed" an account they never got a token for, and
// "the account remains disabled" about an account directory sync had re-enabled. Each rule
// below is general; that case is only where it was found.
//
// The server re-checks every claim made from these against the pinned run blob
// (ExecutiveReportHelper.psm1, Confirm-ExecReportAttackerAccess). KEEP THE TWO IN STEP.

import { refusedAttemptKeys, isTokenIssuingSignIn, PLACEHOLDER_CORRELATION_ID } from './refusedAttempts.js';

const corr = (e) => String(e?.CorrelationId || '').trim().toLowerCase();
const attemptKey = (e) => `${corr(e)}|${String(e?.IPAddress || '').toLowerCase()}`;
const GUID = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

function parseSteps(e) {
  const raw = e?.AuthenticationDetails;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function parseMfaMethod(e) {
  const raw = e?.MfaDetail;
  if (!raw) return '';
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return String(v?.authMethod || '').trim();
  } catch {
    return '';
  }
}

const isPasswordStep = (s) => String(s?.authenticationMethod || '').toLowerCase() === 'password';
const isPushMethod = (m) => /mobile app notification|authenticator app.*notification|push/i.test(String(m || ''));

/**
 * Did the attacker get a token, and what did their attempts expose?
 *
 * @param attackerSignIns  sign-in rows attributed to the attacker (malicious address,
 *                         anchoring leg, or marked session), failed rows INCLUDED
 * @param allSignIns       every sign-in row in the case (refused attempts are keyed off it)
 * @param otherActivityCount  attacker activity that is NOT an interactive sign-in: rows from
 *                         an attacker address in any other workload, non-interactive token
 *                         use, analyst- or session-marked rows. Any of these means a token
 *                         existed, so it rules out "no token was issued".
 * @returns {{
 *   attemptCount: number,          attacker sign-in attempts (distinct transactions)
 *   tokenIssuingCount: number,     attacker sign-ins that issued a token
 *   noTokenIssued: boolean,        attempts exist, none issued a token, and nothing else was done
 *   passwordAccepted: boolean, passwordAcceptedAt: string,
 *   mfaCompleted: boolean, mfaCompletedAt: string, mfaCompletedIp: string,
 *   mfaPush: boolean,              the completed step's OWN records name an app notification
 *   mfaChallenged: boolean,        an attempt reached the second-factor stage (approved or not)
 *   mfaChallengePush: boolean,     an attempt's own records name an app notification as the challenge
 *   firstAttemptAt, lastAttemptAt: span of every attempt row, refused ones included
 *   requestedApps: string[],       real applications the attempts asked for
 * }}
 */
export function assessAttackerAccess({ attackerSignIns = [], allSignIns = [], otherActivityCount = 0 } = {}) {
  const refused = refusedAttemptKeys(allSignIns);
  const tokenIssuing = attackerSignIns.filter((e) => isTokenIssuingSignIn(e, refused));

  // Attempts are TRANSACTIONS: one sign-in writes a Login:login row, an Entra row and a run of
  // SAS:* steps under one CorrelationId. A row with no usable id is its own attempt.
  const attempts = new Map();
  let anon = 0;
  for (const e of attackerSignIns) {
    const c = corr(e);
    const key = c && c !== PLACEHOLDER_CORRELATION_ID ? attemptKey(e) : `row:${e.EventId || `#${anon++}`}`;
    if (!attempts.has(key)) attempts.set(key, []);
    attempts.get(key).push(e);
  }

  // Password accepted: Entra's own step record says so ("Correct password"), or the attempt
  // reached the MFA stage at all (SAS:* rows), which only follows an accepted first factor.
  let passwordAt = '';
  // MFA completed: the attempt reached SAS:ProcessAuth (which only follows a completed second
  // factor), or a non-password step is recorded as succeeded.
  let mfaAt = '';
  let mfaIp = '';
  let mfaPush = false;
  // MFA challenged: an attempt reached the second-factor stage at all -- a SAS:* step, or an
  // MFA method named on its own records. Whether it was then APPROVED is `mfaCompleted`.
  let mfaChallenged = false;
  let challengeMethod = '';
  // The span of the ATTEMPTS, failed ones included. The attacker window is built from
  // "successful" rows, which for a refused relay are the MFA-wait steps, so it ends before
  // the last refused attempt (IR-20261021-ehribik-001: window 12:57, last attempt 12:58:05).
  let firstAttemptAt = '';
  let lastAttemptAt = '';
  const requested = new Set();
  // The addresses the attempts came from. "From N addresses classified malicious" counted
  // every classified address in the case, not the ones that made attempts (v1.203.0).
  const attemptIps = new Set();
  for (const rows of attempts.values()) {
    for (const e of rows) { const ip = String(e?.IPAddress || '').trim(); if (ip) attemptIps.add(ip); }
    const sorted = [...rows].sort((a, b) => String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')));
    let pwd = '';
    let mfa = '';
    let method = '';
    for (const e of sorted) {
      const t = String(e.EventTimeUTC || '');
      if (t && (!firstAttemptAt || t < firstAttemptAt)) firstAttemptAt = t;
      if (t && (!lastAttemptAt || t > lastAttemptAt)) lastAttemptAt = t;
      const named = parseMfaMethod(e)
        || parseSteps(e).find((s) => !isPasswordStep(s) && s.authenticationMethod)?.authenticationMethod
        || '';
      if (/^SAS:/i.test(String(e.RequestType || '')) || named) {
        mfaChallenged = true;
        if (named && !challengeMethod) challengeMethod = named;
      }
      const steps = parseSteps(e);
      if (!pwd && steps.some((s) => isPasswordStep(s) && s.succeeded === true)) pwd = e.EventTimeUTC || '';
      if (!pwd && /^SAS:/i.test(String(e.RequestType || ''))) pwd = e.EventTimeUTC || '';
      const done = steps.find((s) => !isPasswordStep(s) && s.succeeded === true && s.authenticationMethod);
      if (!mfa && done) { mfa = done.authenticationStepDateTime || e.EventTimeUTC || ''; method = done.authenticationMethod; }
      if (!mfa && /^SAS:ProcessAuth$/i.test(String(e.RequestType || ''))) {
        // The approval itself is the last SAS:EndAuth before ProcessAuth, when there is one.
        const end = sorted.filter((x) => /^SAS:EndAuth$/i.test(String(x.RequestType || ''))
          && String(x.EventTimeUTC || '') <= String(e.EventTimeUTC || '')).pop();
        mfa = end?.EventTimeUTC || e.EventTimeUTC || '';
      }
      const app = String(e.ApplicationId || '').trim();
      const name = String(e.ApplicationName || '').trim();
      if (app && GUID.test(app) && name && name !== 'Unknown') requested.add(name);
    }
    if (mfa && !method) {
      // The completed step's method, from THIS attempt's own records only. Another attempt
      // naming a push does not say how this one was approved.
      for (const e of sorted) {
        const steps = parseSteps(e).filter((s) => !isPasswordStep(s) && s.authenticationMethod);
        const named = steps.find((s) => s.succeeded === true)?.authenticationMethod;
        if (named) { method = named; break; }
      }
    }
    if (pwd && (!passwordAt || pwd < passwordAt)) passwordAt = pwd;
    if (mfa && (!mfaAt || mfa < mfaAt)) {
      mfaAt = mfa;
      mfaIp = String(sorted[0]?.IPAddress || '');
      mfaPush = isPushMethod(method);
    }
  }

  const attemptCount = attempts.size;
  return {
    attemptCount,
    tokenIssuingCount: tokenIssuing.length,
    noTokenIssued: attemptCount > 0 && tokenIssuing.length === 0 && !(otherActivityCount > 0),
    passwordAccepted: !!passwordAt,
    passwordAcceptedAt: passwordAt,
    mfaCompleted: !!mfaAt,
    mfaCompletedAt: mfaAt,
    mfaCompletedIp: mfaIp,
    mfaPush,
    mfaChallenged,
    // "Push" only when an attempt's own records name an app notification.
    mfaChallengePush: isPushMethod(challengeMethod),
    firstAttemptAt,
    lastAttemptAt,
    attemptIps: [...attemptIps].sort(),
    requestedApps: [...requested].sort(),
  };
}

const DISABLE_OP = /^disable account$/i;
const ENABLE_OP = /^enable account$/i;
// Entra Connect's provisioning identities. Their account changes mean the account is
// synchronized from on-premises Active Directory, so a change made in the cloud can be
// overwritten by the next sync.
const DIRECTORY_SYNC_ACTOR = /^(ConnectSyncProvisioning_|Sync_)|On-Premises Directory Synchronization/i;

/**
 * The account's enabled/disabled history and its state at the end of the records.
 * `finalState` is '' when no disable or enable is recorded (nothing can be said).
 */
export function accountStateHistory(directoryAuditEvents = []) {
  const changes = [];
  for (const e of directoryAuditEvents || []) {
    const op = String(e?.Operation || '').trim();
    const type = DISABLE_OP.test(op) ? 'disabled' : (ENABLE_OP.test(op) ? 'enabled' : '');
    if (!type || !e.EventTimeUTC) continue;
    const st = String(e.ResultStatus || e.Status || '').toLowerCase();
    if (st === 'failure' || st === 'failed') continue;
    changes.push({ time: e.EventTimeUTC, type, actor: e.UserPrincipalName || e.InitiatedBy || '' });
  }
  changes.sort((a, b) => a.time.localeCompare(b.time));
  // 🔴 ONE CHANGE RECORDED TWICE IS ONE CHANGE (v1.203.0). Entra can write the same disable
  // twice within seconds; both reports then listed "Disabled Oct 14 11:02 AM by Blackpoint"
  // twice (IR-20261014-lperry-001). Only a same-type, same-actor repeat within a minute is
  // folded -- a later second disable is a real event and stays.
  const deduped = changes.filter((c, i) => {
    const p = changes[i - 1];
    return !(p && p.type === c.type && p.actor === c.actor && Math.abs(Date.parse(c.time) - Date.parse(p.time)) < 60000);
  });
  const last = deduped[deduped.length - 1] || null;
  const isReenable = (c, i) => c.type === 'enabled' && deduped.slice(0, i).some((p) => p.type === 'disabled');
  return {
    changes: deduped,
    finalState: last ? last.type : '',
    lastChangeAt: last ? last.time : '',
    lastChangeBy: last ? last.actor : '',
    reenabled: deduped.some(isReenable),
    // 🔴 A RE-ENABLE BY SYNC, not "sync made some change" (v1.203.0). Every consumer says
    // on-premises AD overrode a cloud disable; a sync DISABLE made both reports tell the
    // client sync had re-enabled an account an administrator re-enabled
    // (IR-20261001-ahayman-001, IR-20261014-lperry-001).
    viaDirectorySync: deduped.some((c, i) => isReenable(c, i) && DIRECTORY_SYNC_ACTOR.test(c.actor)),
  };
}

/** True when a password reset or change is recorded AFTER `iso`. */
export function passwordChangedAfter(containmentEvents = [], iso = '') {
  if (!iso) return false;
  return (containmentEvents || []).some((c) => c.type === 'password_reset' && String(c.time) > String(iso));
}

// Consumer and free mail domains: a shared domain there says nothing about a relationship.
const PUBLIC_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com',
  'ymail.com', 'aol.com', 'icloud.com', 'me.com', 'mac.com', 'proton.me', 'protonmail.com',
  'gmx.com', 'gmx.net', 'mail.com', 'zoho.com', 'yandex.com', 'comcast.net', 'att.net', 'verizon.net',
]);

export function mailDomain(address) {
  const a = String(address || '').trim().toLowerCase();
  const at = a.lastIndexOf('@');
  return at > 0 ? a.slice(at + 1) : '';
}

/**
 * Earlier mail between this mailbox and the phishing email's sender ORGANIZATION (its mail
 * domain), BEFORE the lure arrived. On IR-20261013-levig-001 the lure came from
 * jannelle@drbelectric.com inside a thread the user had with carlosb@drbelectric.com, so an
 * exact-address match would have missed it. A lure that arrives inside an existing
 * correspondence means that organization's email MAY be compromised -- a possibility to raise,
 * never a finding. Public mail domains and the mailbox's own domains never count.
 */
export function priorCorrespondence(messageTraceEvents = [], { sender = '', before = '', mailboxAddresses = new Set() } = {}) {
  const dom = mailDomain(sender);
  if (!dom || !before || PUBLIC_MAIL_DOMAINS.has(dom)) return 0;
  const own = new Set([...mailboxAddresses].map(mailDomain).filter(Boolean));
  if (own.has(dom)) return 0;
  const isMine = (addr) => (mailboxAddresses.size ? mailboxAddresses.has(addr) : true);
  let n = 0;
  for (const m of messageTraceEvents || []) {
    const t = String(m?.EventTimeUTC || '');
    if (!t || t >= before) continue;
    const from = String(m.SenderAddress || '').trim().toLowerCase();
    const to = String(m.RecipientAddress || '').trim().toLowerCase();
    if ((mailDomain(from) === dom && isMine(to)) || (mailDomain(to) === dom && isMine(from))) n += 1;
  }
  return n;
}

/**
 * What remains exposed after the incident, from facts the records establish. Each entry is
 * stated only when its condition is TRUE, in the wording the operator approved (2026-10-15):
 *
 *   password_known   — an attacker attempt was given the correct password AND no password
 *                      reset or change is recorded after it. "The attacker has the password"
 *                      is wrong once the password has been changed, so the reset check is the
 *                      condition, not a caveat.
 *   mfa_approved     — an attacker attempt completed multi-factor authentication. The user
 *                      approved a request they did not start (the attempt was the attacker's).
 *                      "Push" only when THAT attempt's own records name an app notification.
 *   sender_mailbox   — the phishing email came from an organization this mailbox was already
 *                      corresponding with. Its email MAY be compromised: a possibility, never a
 *                      finding.
 *
 * Returns raw ISO times; each consumer formats them in its own timezone.
 */
export function buildExposures({
  attackerWindow = null, messageTraceEvents = [], phishingFlags = [], mailboxAddresses = new Set(),
} = {}) {
  const out = [];
  const acc = attackerWindow?.attackerAccess;
  if (acc?.passwordAccepted && !passwordChangedAfter(attackerWindow?.containmentEvents, acc.passwordAcceptedAt)) {
    out.push({
      id: 'password_known',
      at: acc.passwordAcceptedAt,
      directorySynced: !!attackerWindow?.accountState?.viaDirectorySync,
    });
  }
  if (acc?.mfaCompleted) {
    out.push({ id: 'mfa_approved', at: acc.mfaCompletedAt, ip: acc.mfaCompletedIp, push: !!acc.mfaPush });
  }
  const byId = new Map((messageTraceEvents || []).map((m) => [String(m?.EventId || ''), m]));
  const seen = new Set();
  for (const f of phishingFlags || []) {
    const row = byId.get(String(f?.eventId || ''));
    const sender = String(f?.senderAddress || row?.SenderAddress || '').trim().toLowerCase();
    const domain = mailDomain(sender);
    if (!domain || seen.has(domain) || !row?.EventTimeUTC) continue;
    const prior = priorCorrespondence(messageTraceEvents, { sender, before: row.EventTimeUTC, mailboxAddresses });
    if (prior > 0) {
      seen.add(domain);
      out.push({ id: 'sender_mailbox', sender, domain, priorMessages: prior });
    }
  }
  return out;
}

/** A URL recorded where an application should be: the phishing kit's own page. */
export function isUrlShapedApp(name) {
  return /^https?:\/\//i.test(String(name || '').trim());
}
